import { Badge, BadgeProps } from "@/components/badge";
import { LoadingSpinner } from "@/components/loading-spinner";
import { useExperimentVersions } from "@/http/api-hooks";
import { ExperimentVersion } from "@/http/api-types";
import { cn, formatUTCDateTime } from "@/lib/utils";
import { ArrowDown, Clock3, History, UserRound } from "lucide-react";
import { getHistoryChangeRows, getHistoryDiffLines, getVersionEventLabel, HistoryChangeRow } from "./history-tab";

type HistoryTabProps = {
  experimentId: number;
};

export function HistoryTab({ experimentId }: HistoryTabProps) {
  const { data: versions, isLoading, error } = useExperimentVersions(experimentId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <div className="py-12 text-center text-sm text-red-600">Failed to load experiment history.</div>;
  }

  if (!versions || versions.length === 0) {
    return (
      <section>
        <header className="mb-2 flex items-center gap-2 border-b border-border pb-2">
          <History className="h-5 w-5" />
          <h3 className="text-lg font-semibold tracking-tight">History</h3>
        </header>
        <p className="mb-6 text-sm text-muted-foreground">
          Tracked experiment changes will appear here after the next edit.
        </p>
        <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No changes have been tracked for this experiment yet.
        </div>
      </section>
    );
  }

  return (
    <section>
      <header className="mb-2 flex items-center gap-2 border-b border-border pb-2">
        <History className="h-5 w-5" />
        <h3 className="text-lg font-semibold tracking-tight">History</h3>
      </header>
      <p className="mb-6 text-sm text-muted-foreground">
        Audit trail of experiment setup, summary, and variant configuration changes.
      </p>
      <div className="relative space-y-8 sm:pl-6">
        <div className="absolute bottom-3 left-2 top-3 hidden w-px bg-border sm:block" />
        {versions.map(version => (
          <VersionEntry key={version.id} version={version} />
        ))}
      </div>
    </section>
  );
}

function VersionEntry({ version }: { version: ExperimentVersion }) {
  const changes = getHistoryChangeRows(version);
  const actorName = version.actor_name || "Unknown user";

  return (
    <section className="relative min-w-0 overflow-hidden rounded-lg border border-border bg-muted/20 p-5 shadow-sm">
      <div className="absolute -left-[1.18rem] top-6 hidden h-3 w-3 rounded-full border-2 border-background bg-primary sm:block" />
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={eventBadgeVariant(version.event)}>{getVersionEventLabel(version.event)}</Badge>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1 font-semibold">
              <UserRound className="h-3.5 w-3.5" />
              {actorName}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Clock3 className="h-3.5 w-3.5" />
              {formatUTCDateTime(version.occurred_at)}
            </span>
          </div>
        </div>
        <span className="text-xs font-medium text-muted-foreground">{changeCountLabel(changes.length)}</span>
      </div>

      {changes.length > 0 ? (
        <div className="mt-4 space-y-3">
          {changes.map(change => (
            <ChangeRow key={change.key} change={change} event={version.event} />
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-md bg-background/60 px-3 py-2 text-sm text-muted-foreground">
          No field-level changes were recorded.
        </div>
      )}
    </section>
  );
}

function ChangeRow({ change, event }: { change: HistoryChangeRow; event: ExperimentVersion["event"] }) {
  const diffLines = getHistoryDiffLines(change, event);

  return (
    <div className="min-w-0 rounded-lg bg-background/70 p-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium" title={change.label}>
          {change.label}
        </div>
        <div className="mt-1 break-all font-mono text-xs text-muted-foreground">{change.fieldPath}</div>
      </div>

      <div className="mt-3 min-w-0 overflow-hidden rounded-md font-mono text-xs">
        {diffLines.map((line, index) => {
          const previousLine = diffLines[index - 1];
          const showTransition = previousLine?.kind === "removed" && line.kind === "added";

          return (
            <div key={`${line.kind}-${index}`}>
              {showTransition && (
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <div className="h-px flex-1 bg-border" />
                  <span className="inline-flex items-center gap-1">
                    <ArrowDown className="h-3 w-3" />
                    changed to
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <div
                className={cn(
                  "grid min-w-0 grid-cols-[2rem_minmax(0,1fr)] border-l-2",
                  line.kind === "removed" &&
                    "border-red-300 bg-red-50 text-red-950 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100",
                  line.kind === "added" &&
                    "border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-100",
                )}
              >
                <span
                  className={cn(
                    "select-none py-1 text-center",
                    line.kind === "removed" && "bg-red-100/70 text-red-700 dark:bg-red-950/50",
                    line.kind === "added" && "bg-emerald-100/70 text-emerald-700 dark:bg-emerald-950/50",
                  )}
                >
                  {line.prefix}
                </span>
                <span className="min-w-0 whitespace-pre-wrap break-words px-3 py-1">{line.value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function eventBadgeVariant(event: ExperimentVersion["event"]): BadgeProps["variant"] {
  switch (event) {
    case "create":
      return "active";
    case "delete":
      return "destructive";
    case "update":
      return "scheduled";
    default:
      return "outline";
  }
}

function changeCountLabel(count: number): string {
  if (count === 1) {
    return "1 field changed";
  }
  return `${count} fields changed`;
}
